import { Activity, Coins } from "lucide-react"
import * as React from "react"
import { Link } from "react-router-dom"
import { toast } from "sonner"

import { PageHeader } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { ApiError, api } from "@/lib/api"
import type { AdminSpend, AdminSpendRow } from "@/lib/types"
import { formatCostUsd } from "@/lib/utils"

const n = (value: number) => value.toLocaleString()

const DAY_OPTIONS = ["7", "30", "90"] as const

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string | number
  hint?: string
}) {
  return (
    <Card>
      <CardContent className="flex items-start gap-3 p-4">
        <div className="bg-muted text-muted-foreground rounded-md p-2">
          <Icon className="size-4" />
        </div>
        <div className="min-w-0">
          <div className="text-muted-foreground text-xs font-medium">{label}</div>
          <div className="text-2xl font-semibold leading-tight">{value}</div>
          {hint && <div className="text-muted-foreground mt-0.5 text-xs">{hint}</div>}
        </div>
      </CardContent>
    </Card>
  )
}

function SpendTable({ title, rows }: { title: string; rows: AdminSpendRow[] }) {
  return (
    <div>
      <h2 className="mb-3 text-sm font-semibold">{title}</h2>
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">No spend in this window.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Name</th>
                <th className="px-4 py-2 text-right font-medium">Runs</th>
                <th className="px-4 py-2 text-right font-medium">Tokens</th>
                <th className="px-4 py-2 text-right font-medium">Est. cost</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label} className="border-t">
                  <td className="px-4 py-2">{row.label}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{n(row.runs)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{n(row.total_tokens)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatCostUsd(row.cost_usd)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export function AdminSpendPage() {
  const [days, setDays] = React.useState("30")
  const [spend, setSpend] = React.useState<AdminSpend | null>(null)
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    setLoading(true)
    api
      .adminSpend(Number(days))
      .then(setSpend)
      .catch((error) =>
        toast.error(error instanceof ApiError ? error.message : "Could not load spend data."),
      )
      .finally(() => setLoading(false))
  }, [days])

  return (
    <>
      <PageHeader
        title="Spending"
        description="Estimated spend across every account, for cost monitoring and debugging."
        action={
          <div className="flex items-center gap-2">
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAY_OPTIONS.map((d) => (
                  <SelectItem key={d} value={d}>
                    Last {d} days
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button asChild variant="outline">
              <Link to="/admin">Back to admin</Link>
            </Button>
          </div>
        }
      />
      <div className="space-y-6 p-6">
        {loading || !spend ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <StatCard
                icon={Coins}
                label="Estimated spend"
                value={formatCostUsd(spend.totals.cost_usd)}
                hint={`over the last ${spend.days} days`}
              />
              <StatCard icon={Activity} label="Runs" value={n(spend.totals.runs)} />
              <StatCard icon={Coins} label="Tokens" value={n(spend.totals.total_tokens)} />
            </div>
            <p className="text-muted-foreground text-xs">
              Cost is an estimate from listed model prices; usage on unpriced models counts tokens
              but adds $0.
            </p>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <SpendTable title="By user" rows={spend.by_user} />
              <SpendTable title="By agent" rows={spend.by_agent} />
            </div>
          </>
        )}
      </div>
    </>
  )
}
